import React from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getTransportationById, deleteTransportation } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButton,
  ActionButtonDanger
} from '../../components/layout';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { Edit, Trash2, DollarSign, Phone, Calendar, User, Car, MapPin, Users, Clock } from 'lucide-react';
import { isAdministrator } from '../../client/lib/platformRole';

export default function ViewTransportationPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { itemId, slug } = useParams<{ itemId: string; slug?: string }>();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const transportationListPath = locationSectionPath(locSlug, 'transportation');

  const { data: item, isLoading, error } = useQuery(getTransportationById, { itemId: itemId || '' });
  const deleteTransportationFn = useAction(deleteTransportation);

  const handleGoBack = () => {
    navigate(transportationListPath);
  };

  const handleEdit = () => {
    navigate(locationSectionPath(locSlug, 'transportation', itemId!, 'edit'));
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteTransportationFn({ itemId: itemId || '' });
        handleGoBack();
      } catch (error: unknown) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Item Not Found</h2>
            <button onClick={handleGoBack} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Go Back</button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isOwner = !!user && ((item as any).authorId === user.id || isAdministrator(user));
  const currentPageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const formatScheduleType = (scheduleType: string) => scheduleType?.replace('_', ' ') || '-';

  const breadcrumbItems = [
    {
      label: 'Community',
      dropdownItems: [
        { label: 'Buy and Sell', href: locationSectionPath(locSlug, 'buy-sell') },
        { label: 'Transportation', href: transportationListPath },
        { label: 'Housing', href: locationSectionPath(locSlug, 'housing') },
      ]
    },
    {
      label: 'Transportation',
      isClickable: true,
      href: transportationListPath
    }
  ];

  const sidebarContent = (
    <>
      {isOwner && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButton onClick={handleEdit} icon={Edit}>Edit Item</ActionButton>
            <ActionButtonDanger onClick={handleDelete} icon={Trash2}>Delete Item</ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Type" value={(item as any).metadata?.listingType === 'OFFER' ? 'Offering Ride' : 'Requesting Ride'} />
          {(item as any).metadata?.scheduleType && (
            <DetailField label="Schedule" value={formatScheduleType((item as any).metadata.scheduleType)} />
          )}
          {(item as any).metadata?.vehicleType && (
            <DetailField label="Vehicle" value={(item as any).metadata.vehicleType} />
          )}
          {(item as any).metadata?.seats && (
            <DetailField label="Seats Available" value={String((item as any).metadata.seats)} />
          )}
          <DetailField label="Posted by" value={(item as any).author?.name || (item as any).author?.email || ''} />
          <DetailField label="Posted on" value={new Date((item as any).createdAt).toLocaleDateString()} />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className={`px-3 py-1 text-sm font-medium rounded ${
          (item as any).metadata?.listingType === 'OFFER'
            ? 'bg-green-100 text-green-800'
            : 'bg-blue-100 text-blue-800'
        }`}>
          {(item as any).metadata?.listingType === 'OFFER' ? 'Offering Ride' : 'Requesting Ride'}
        </span>
        {(item as any).metadata?.scheduleType && (
          <span className="px-3 py-1 text-sm font-medium rounded bg-purple-100 text-purple-800">
            {formatScheduleType((item as any).metadata.scheduleType)}
          </span>
        )}
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-4">{(item as any).title}</h1>

      {((item as any).metadata?.routeFrom || (item as any).metadata?.routeTo) && (
        <div className="flex items-center gap-2 text-lg text-gray-600 mb-4">
          <MapPin className="w-5 h-5 text-gray-400" />
          <span>{(item as any).metadata?.routeFrom} → {(item as any).metadata?.routeTo}</span>
        </div>
      )}

      {(item as any).metadata?.price && (
        <div className="text-3xl font-bold text-green-600 mb-6">
          ${(item as any).metadata.price} <span className="text-lg font-normal text-gray-500">per trip/seat</span>
        </div>
      )}

      {/* Description */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
        <div className="prose max-w-none">
          <p className="text-gray-700 whitespace-pre-wrap">{(item as any).description}</p>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ride Information</h3>
          <dl className="space-y-3">
            <div className="flex items-center gap-3">
              <Car className="w-5 h-5 text-gray-400" />
              <dt className="text-sm font-medium text-gray-500">Type:</dt>
              <dd className="text-sm text-gray-900">{(item as any).metadata?.listingType === 'OFFER' ? 'Offering Ride' : 'Requesting Ride'}</dd>
            </div>

            {(item as any).metadata?.scheduleType && (
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Schedule:</dt>
                <dd className="text-sm text-gray-900">{formatScheduleType((item as any).metadata.scheduleType)}</dd>
              </div>
            )}

            {(item as any).metadata?.schedule && (
              <div className="flex items-center gap-3">
                <dt className="text-sm font-medium text-gray-500">When:</dt>
                <dd className="text-sm text-gray-900">{(item as any).metadata.schedule}</dd>
              </div>
            )}

            {(item as any).metadata?.vehicleType && (
              <div className="flex items-center gap-3">
                <dt className="text-sm font-medium text-gray-500">Vehicle:</dt>
                <dd className="text-sm text-gray-900">{(item as any).metadata.vehicleType}</dd>
              </div>
            )}

            {(item as any).metadata?.seats && (
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Seats Available:</dt>
                <dd className="text-sm text-gray-900">{(item as any).metadata.seats}</dd>
              </div>
            )}

            {(item as any).metadata?.price && (
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Price:</dt>
                <dd className="text-sm text-gray-900">${(item as any).metadata.price}</dd>
              </div>
            )}
          </dl>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
          <dl className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-400" />
              <dt className="text-sm font-medium text-gray-500">Posted by:</dt>
              <dd className="text-sm text-gray-900">{(item as any).author?.name || (item as any).author?.email}</dd>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <dt className="text-sm font-medium text-gray-500">Posted on:</dt>
              <dd className="text-sm text-gray-900">{new Date((item as any).createdAt).toLocaleDateString()}</dd>
            </div>

            {(item as any).metadata?.contactInfo && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Contact:</dt>
                <dd className="text-sm text-gray-900">{(item as any).metadata.contactInfo}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </IndividualViewPageLayout>
  );
}



